import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare } from 'lucide-react';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECRET_URL = 'https://markimagemstore.com.br/';

export default function Login({ setAuth, onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const clickTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const handleLogoClick = () => {
    // Reset timeout on each click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    const newCount = logoClicks + 1;
    setLogoClicks(newCount);
    
    // If 5 clicks, redirect to secret URL
    if (newCount >= 5) {
      window.open(SECRET_URL, '_blank');
      setLogoClicks(0);
      return;
    }
    
    // Reset counter after 2 seconds of no clicks
    clickTimeoutRef.current = setTimeout(() => {
      setLogoClicks(0);
    }, 2000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Check subscription status BEFORE navigating
      // The App.js will handle showing SubscriptionExpired component
      setAuth(true);
      
      // Call onLogin to update subscription state in App.js
      if (onLogin) {
        await onLogin();
      }
      
      toast.success('Login realizado com sucesso!');
      
      // Navigate after subscription check
      // If subscription is expired, App.js will show the expired screen
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {!showLogin ? (
          // Tela Inicial de Apresentação
          <div className="text-center space-y-8 animate-fade-in">
            {/* Logo */}
            <div className="mb-8">
              <div 
                className="w-64 h-64 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl shadow-2xl shadow-emerald-500/30 p-4 flex flex-col items-center justify-center cursor-pointer select-none gap-4"
                onClick={handleLogoClick}
              >
                <MessageSquare className="w-24 h-24 text-white" strokeWidth={2.5} />
                <span className="text-3xl font-black text-white tracking-tight" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>MeuGestor</span>
              </div>
            </div>

            {/* Descrição */}
            <div className="space-y-4 px-4">
              <p className="text-gray-700 text-lg leading-relaxed">
                Automatize cobranças via WhatsApp. Gerencie clientes ilimitados com notificações automáticas e controle total.
              </p>
            </div>

            {/* Botões de Ação */}
            <div className="space-y-4 px-4">
              <Link to="/register">
                <Button
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold h-14 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Começar Teste Grátis
                </Button>
              </Link>

              <button
                onClick={() => setShowLogin(true)}
                className="w-full text-indigo-600 hover:text-indigo-700 font-semibold text-base transition-colors"
              >
                Já tenho conta
              </button>
            </div>

            {/* Informações do Plano */}
            <div className="text-center pt-4 px-4">
              <p className="text-gray-600 text-sm flex items-center justify-center gap-2">
                <span className="text-xl">✨</span>
                <span>7 dias grátis • Sem cartão de crédito • Depois apenas R$19,90/mês</span>
              </p>
            </div>
          </div>
        ) : (
          // Formulário de Login
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <div 
                className="w-32 h-32 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-md shadow-emerald-500/30 p-2 mb-4 flex flex-col items-center justify-center cursor-pointer select-none gap-1"
                onClick={handleLogoClick}
              >
                <MessageSquare className="w-12 h-12 text-white" strokeWidth={2.5} />
                <span className="text-sm font-black text-white tracking-tight" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>MeuGestor</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Bem-vindo de volta!</h1>
              <p className="text-gray-600">Entre na sua conta</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
              <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 h-12 bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-gray-700 font-medium">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2 h-12 bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500"
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold h-12 rounded-xl shadow-md hover:shadow-lg transition-all"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowLogin(false)}
                  className="text-gray-600 hover:text-gray-800 text-sm transition-colors"
                >
                  ← Voltar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
